import {
  formatE164,
  mergeValues,
  partnerDisplayName,
  renderPartnerMessage,
  smsSegments,
  toE164,
  whyNotTextable,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { useAsync } from "../../lib/hooks";
import {
  insertMessage,
  insertReferral,
  markSent,
  fetchTemplates,
  type Partner,
} from "../../lib/partnerQueries";
import { fetchContacts } from "../../lib/queries";
import { SEND_CHANNEL, send } from "../../lib/sendChannel";
import { useSession } from "../../session";

/**
 * Handing a customer to a partner, or recording one they handed us.
 *
 * The two directions share a form because they are the same fact recorded from
 * opposite ends, and the scoreboard on the partner page is a comparison — so
 * splitting them into two screens would make the one useful number a join.
 *
 * The direction that goes *out* has a consent gate of its own, and it is a
 * different consent from the partner's: handing somebody's phone number to a
 * realtor is sharing a customer's details with a third party, and the customer
 * has to have said it was fine. Same rule as the partner's own opt-in — asked
 * out loud, recorded here, not inferred.
 */
export function ReferralModal({
  partner,
  direction,
  onClose,
  onDone,
}: {
  partner: Partner;
  direction: "to_partner" | "from_partner";
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { profileId, displayName } = useSession();
  const contactsQuery = useAsync(fetchContacts, []);
  const templatesQuery = useAsync(fetchTemplates, []);

  const [contactId, setContactId] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [context, setContext] = useState("");
  const [notes, setNotes] = useState("");
  const [customerAgreed, setCustomerAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const outgoing = direction === "to_partner";

  // Picking a customer fills the boxes but does not lock them: the referral is
  // what was said out loud on a particular day, and the name said out loud has
  // to survive the customer record being renamed later.
  function pickCustomer(id: string): void {
    setContactId(id);
    const contact = (contactsQuery.data ?? []).find((row) => row.id === id);
    if (!contact) return;
    setName(contact.full_name ?? "");
    setPhone(contact.cell ?? contact.phone ?? "");
    setEmail(contact.email ?? "");
  }

  const introTemplate = (templatesQuery.data ?? []).find(
    (row) => row.kind === "referral_intro" && row.active,
  );

  const body = useMemo(() => {
    if (!outgoing || !introTemplate) return "";
    return renderPartnerMessage(
      introTemplate.body,
      mergeValues({
        partner,
        repFirstName: displayName.split(" ")[0] ?? "",
        customerName: name,
        customerPhone: formatE164(toE164(phone)) ?? phone,
        oneLineContext: context,
      }),
    );
  }, [outgoing, introTemplate, partner, displayName, name, phone, context]);

  const [edited, setEdited] = useState<string | null>(null);
  const message = edited ?? body;

  const blocked = whyNotTextable(partner);
  const canSave =
    name.trim() !== "" && (!outgoing || customerAgreed) && !saving;

  async function save(alsoText: boolean): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await insertReferral({
        partner_id: partner.id,
        direction,
        customer_name: name.trim(),
        customer_phone: toE164(phone) ?? (phone.trim() || null),
        customer_email: email.trim() || null,
        contact_id: contactId || null,
        notes:
          [context.trim(), notes.trim()].filter(Boolean).join(" — ") || null,
        created_by: profileId,
      });

      if (alsoText && outgoing) {
        const row = await insertMessage({
          partner_id: partner.id,
          kind: "referral_intro",
          template_id: introTemplate?.id ?? null,
          body: message.trim(),
          status: "approved",
        });
        await markSent(row.id, profileId);
        await onDone();
        const outcome = send(
          SEND_CHANNEL,
          partner.phone_mobile,
          message.trim(),
        );
        if (outcome.state === "refused") setError(outcome.reason);
        return;
      }

      await onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <Modal
      title={
        outgoing
          ? `Refer a customer to ${partnerDisplayName(partner)}`
          : `Log a referral from ${partnerDisplayName(partner)}`
      }
      subtitle={
        outgoing
          ? "Records it, and can open Messages with the introduction ready."
          : "Records that they sent us somebody, so the scoreboard is honest."
      }
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-ghost"
            disabled={!canSave}
            onClick={() => void save(false)}
          >
            {outgoing ? "Just log it" : "Log it"}
          </button>
          {outgoing && (
            <button
              className="btn-primary"
              disabled={!canSave || Boolean(blocked) || message.trim() === ""}
              title={blocked ?? "Opens Messages with the introduction"}
              onClick={() => void save(true)}
            >
              Log it and open Messages
            </button>
          )}
        </>
      }
    >
      <div className="fieldgrid">
        <div className="field full">
          <label htmlFor="refcust">Customer in Roof Board</label>
          <select
            id="refcust"
            value={contactId}
            onChange={(e) => pickCustomer(e.target.value)}
          >
            <option value="">Not one of ours — type the details</option>
            {(contactsQuery.data ?? []).map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.full_name}
              </option>
            ))}
          </select>
        </div>

        <div className="field full">
          <label htmlFor="refname">
            Name <span className="req">*</span>
          </label>
          <input
            id="refname"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="refphone">Phone</label>
          <input
            id="refphone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="refemail">Email</label>
          <input
            id="refemail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {outgoing && (
          <div className="field full">
            <label htmlFor="refcontext">One line about them</label>
            <input
              id="refcontext"
              value={context}
              placeholder="Selling in Chelsea, wants it listed by October."
              onChange={(e) => {
                setContext(e.target.value);
                setEdited(null);
              }}
            />
          </div>
        )}

        <div className="field full">
          <label htmlFor="refnotes">Notes</label>
          <textarea
            id="refnotes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      {outgoing && (
        <>
          <label className="consent-check">
            <input
              type="checkbox"
              checked={customerAgreed}
              onChange={(e) => setCustomerAgreed(e.target.checked)}
            />
            <span>
              {name.trim() || "The customer"} said it&apos;s OK to pass their
              details on.
            </span>
          </label>

          <div className="field full">
            <label htmlFor="refbody">The introduction</label>
            <textarea
              id="refbody"
              rows={4}
              value={message}
              onChange={(e) => setEdited(e.target.value)}
            />
            <span className="sub">
              {message.trim().length} characters · {smsSegments(message)}{" "}
              {smsSegments(message) === 1 ? "segment" : "segments"}
            </span>
          </div>
        </>
      )}

      <ErrorNote error={error} />
    </Modal>
  );
}
